import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
export function AbsorptionComparisonGraph() {
  const [activeProduct, setActiveProduct] = useState<string | null>(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setAnimationComplete(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  const products = [{
    name: 'LyraSleep',
    color: 'from-purple-500 to-indigo-600',
    peakTime: 20,
    peakValue: 95,
    description: 'Reaches peak absorption in 15-20 minutes',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  }, {
    name: 'Melatonin',
    color: 'from-blue-400 to-blue-500',
    peakTime: 50,
    peakValue: 70,
    description: 'Takes 45-60 minutes to reach peak',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  }, {
    name: 'Ambien',
    color: 'from-gray-400 to-gray-500',
    peakTime: 60,
    peakValue: 85,
    description: 'Takes 60+ minutes with side effects',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  }];
  return <div className="my-16 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border-2 border-purple-100" data-id="element-1">
      <div className="text-center mb-8" data-id="element-2">
        <div className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-3">
          <TrendingUp className="w-4 h-4" data-id="element-4" />
          ABSORPTION SPEED COMPARISON
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-5">
          LyraSleep Works 3-4x Faster
        </h3>
        <p className="text-gray-600" data-id="element-6">
          Clinical absorption rates compared over 60 minutes
        </p>
      </div>

      {/* Graph Container */}
      <div className="bg-white rounded-xl p-6 shadow-lg mb-6" data-id="element-7">
        <div className="relative h-64 md:h-80" data-id="element-8">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-9">
            <span data-id="element-10">100%</span>
            <span data-id="element-11">75%</span>
            <span data-id="element-12">50%</span>
            <span data-id="element-13">25%</span>
            <span data-id="element-14">0%</span>
          </div>

          {/* Grid lines */}
          <div className="absolute left-12 right-0 top-0 bottom-8 flex flex-col justify-between" data-id="element-15">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="border-t border-gray-200" data-id="element-16" />)}
          </div>

          {/* Graph area */}
          <div className="absolute left-12 right-0 top-0 bottom-8" data-id="element-17">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-18">
              {products.map((product, index) => {
              const points = [];
              for (let x = 0; x <= 100; x += 2) {
                const time = x / 100 * 60;
                let y;
                if (time <= product.peakTime) {
                  y = time / product.peakTime * product.peakValue;
                } else {
                  y = product.peakValue - (time - product.peakTime) / (60 - product.peakTime) * (product.peakValue * 0.3);
                }
                points.push(`${x},${100 - y}`);
              }
              return <motion.path key={product.name} d={`M ${points.join(' L ')}`} fill="none" stroke={`url(#gradient-${index})`} strokeWidth={activeProduct === product.name ? '1.5' : '1'} initial={{
                pathLength: 0,
                opacity: 0
              }} animate={{
                pathLength: 1,
                opacity: activeProduct === null || activeProduct === product.name ? 1 : 0.3
              }} transition={{
                duration: 2,
                ease: 'easeInOut'
              }} className="cursor-pointer" onMouseEnter={() => setActiveProduct(product.name)} onMouseLeave={() => setActiveProduct(null)} data-id="element-19" />;
            })}

              {/* Gradients */}
              <defs data-id="element-20">
                {products.map((product, index) => <linearGradient key={index} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-21">
                    <stop offset="0%" stopColor={product.color.split(' ')[0].replace('from-', '')} data-id="element-22" />
                    <stop offset="100%" stopColor={product.color.split(' ')[2]} data-id="element-23" />
                  </linearGradient>)}
              </defs>
            </svg>
          </div>

          {/* X-axis labels */}
          <div className="absolute left-12 right-0 bottom-0 flex justify-between text-xs text-gray-500" data-id="element-24">
            <span data-id="element-25">0 min</span>
            <span data-id="element-26">15 min</span>
            <span data-id="element-27">30 min</span>
            <span data-id="element-28">45 min</span>
            <span data-id="element-29">60 min</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="grid md:grid-cols-3 gap-4" data-id="element-30">
        {products.map(product => <motion.div key={product.name} className={`${product.bgColor} ${product.borderColor} border-2 rounded-xl p-4 cursor-pointer transition-all`} whileHover={{
        scale: 1.02
      }} onMouseEnter={() => setActiveProduct(product.name)} onMouseLeave={() => setActiveProduct(null)} data-id="element-31">
            <div className="flex items-center gap-2 mb-2" data-id="element-32">
              <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${product.color}`} data-id="element-33" />
              <span className="font-bold text-gray-900" data-id="element-34">{product.name}</span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-35">{product.description}</p>
            {product.name === 'LyraSleep' && <div className="mt-2 inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold" data-id="element-36">
                ⚡ 3-4x Faster
              </div>}
          </motion.div>)}
      </div>
    </div>;
}